import React, { useState, useEffect, useRef, useMemo } from 'react';
import { useSearchParams, Link, useLocation } from 'react-router-dom';
import { providersAPI } from '../api/providers';
import Spinner from '../components/common/Spinner';
import { useAuth } from '../hooks/useAuth';
import {
  getFavorites,
  toggleFavorite as toggleFavoriteStorage,
  FAVORITES_EVENT,
} from '../utils/favorites';
import {
  MagnifyingGlassIcon,
  MapPinIcon,
  CheckBadgeIcon,
  ArrowRightIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  HeartIcon as HeartOutline,
  CalendarDaysIcon,
  StarIcon,
  TruckIcon,
  Squares2X2Icon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { StarIcon as StarSolid, HeartIcon as HeartSolid } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';

gsap.registerPlugin(ScrollTrigger, ScrollToPlugin);

const RESULTS_PER_PAGE = 6;

const FALLBACK_SERVICES = [
  { id: 'hair', name: 'Hair Styling', desc: 'Cuts, coloring, blowouts, and treatments', providers: 48 },
  { id: 'nails', name: 'Nail Care', desc: 'Manicures, pedicures, gel and nail art', providers: 32 },
  { id: 'makeup', name: 'Makeup Artistry', desc: 'Bridal, editorial, and everyday looks', providers: 21 },
  { id: 'facial', name: 'Facials & Skincare', desc: 'Cleansing, treatments, and anti-aging', providers: 18 },
  { id: 'massage', name: 'Massage & Spa', desc: 'Relaxation, deep tissue, and wellness', providers: 15 },
  { id: 'waxing', name: 'Waxing & Hair Removal', desc: 'Professional waxing and depilation', providers: 12 },
  { id: 'barber', name: 'Barbering', desc: 'Men’s cuts, shaves, and beard care', providers: 24 },
];

const Search = () => {
  const location = useLocation();
  const { user, isAuthenticated } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const [providers, setProviders] = useState([]);
  const [favoriteIds, setFavoriteIds] = useState(() => new Set());
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [openFilter, setOpenFilter] = useState(null); // 'location' | 'rating' | 'type' | 'category'

  // Close active filter popover on outside click
  useEffect(() => {
    if (!openFilter) return;
    const handler = (e) => {
      if (!e.target.closest('[data-filter-pop]')) setOpenFilter(null);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [openFilter]);

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const DESCRIPTION_LIMIT = 120;

  const [filters, setFilters] = useState({
    q: searchParams.get('q') || '',
    category: searchParams.get('category') || '',
    location: searchParams.get('location') || '',
    rating: searchParams.get('rating') || '',
    mobile: searchParams.get('mobile') || '',
  });

  const providersRef = useRef(null);
  const bridgeRef = useRef(null);

  const scrollToProviders = () => {
    if (!providersRef.current) return;
    gsap.to(window, {
      duration: 0.9,
      scrollTo: { y: providersRef.current, offsetY: 24 },
      ease: 'power3.inOut',
    });
  };

  useEffect(() => {
    const shouldScrollToProviders =
      searchParams.get('view') === 'providers' || location.hash === '#providers';

    if (!shouldScrollToProviders || !providersRef.current) return;

    const timer = window.setTimeout(() => {
      scrollToProviders();
    }, 150);

    return () => window.clearTimeout(timer);
  }, [location.hash, searchParams]);

  useEffect(() => {
    providersAPI.getCategories().then((data) => setCategories(data || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (!user?.id) {
      setFavoriteIds(new Set());
      return;
    }
    const sync = () => setFavoriteIds(new Set(getFavorites(user.id)));
    sync();
    const handler = (e) => {
      if (!e.detail || e.detail.userId === user.id) sync();
    };
    window.addEventListener(FAVORITES_EVENT, handler);
    return () => window.removeEventListener(FAVORITES_EVENT, handler);
  }, [user?.id]);

  const handleToggleFavorite = (provider, e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!isAuthenticated || !user?.id) {
      toast.error('Sign in to save favorites');
      return;
    }
    const nowFavorite = !favoriteIds.has(provider.id);
    toggleFavoriteStorage(user.id, provider.id);
    toast.success(
      nowFavorite
        ? `Saved ${provider.businessName || 'provider'} to favorites`
        : `Removed ${provider.businessName || 'provider'} from favorites`
    );
  };

  useEffect(() => {
    fetchProviders();
  }, []);

  useEffect(() => {
    const ctx = gsap.context(() => {
      gsap.from(providersRef.current, {
        y: 30,
        opacity: 0,
        duration: 0.8,
        ease: 'power3.out',
        scrollTrigger: { trigger: providersRef.current, start: 'top 85%' },
      });

      // Bridge — staggered step reveals + animated connector progress
      if (bridgeRef.current) {
        gsap.from(bridgeRef.current.querySelectorAll('.bridge-step'), {
          y: 30,
          opacity: 0,
          scale: 0.92,
          duration: 0.7,
          ease: 'back.out(1.4)',
          stagger: 0.15,
          scrollTrigger: { trigger: bridgeRef.current, start: 'top 80%' },
        });
        gsap.from(bridgeRef.current.querySelectorAll('.bridge-connector'), {
          scaleX: 0,
          duration: 0.8,
          ease: 'power3.out',
          stagger: 0.18,
          delay: 0.25,
          transformOrigin: 'left center',
          scrollTrigger: { trigger: bridgeRef.current, start: 'top 80%' },
        });
      }

    });

    return () => ctx.revert();
  }, []);

  const fetchProviders = async () => {
    setLoading(true);
    try {
      const data = await providersAPI.searchProviders({});
      setProviders(Array.isArray(data) ? data : data.providers || []);
      setPage(1);
    } catch (error) {
      console.error('Error loading providers:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (name, value) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const applyFilters = () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => {
      if (v) params[k] = v;
    });
    setSearchParams(params);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') applyFilters();
  };

  const filteredProviders = useMemo(() => {
    const q = filters.q.trim().toLowerCase();
    const loc = filters.location.trim().toLowerCase();
    const minRating = filters.rating ? parseFloat(filters.rating) : 0;

    return providers.filter((p) => {
      if (q) {
        const name = (p.businessName || '').toLowerCase();
        const desc = (p.description || '').toLowerCase();
        if (!name.includes(q) && !desc.includes(q)) return false;
      }
      if (loc) {
        const pLoc = (p.location || '').toLowerCase();
        if (!pLoc.includes(loc)) return false;
      }
      if (minRating && (p.rating || 0) < minRating) return false;
      if (filters.mobile) {
        const wantMobile = filters.mobile === 'true';
        if (!!p.offersMobile !== wantMobile) return false;
      }
      if (filters.category) {
        const pCat = p.categoryId || p.category?.id || p.category || '';
        if (String(pCat) !== String(filters.category)) return false;
      }
      return true;
    });
  }, [providers, filters]);

  useEffect(() => {
    setPage(1);
  }, [filters.q, filters.location, filters.rating, filters.mobile, filters.category]);

  const totalPages = Math.ceil(filteredProviders.length / RESULTS_PER_PAGE);
  const paginated = filteredProviders.slice((page - 1) * RESULTS_PER_PAGE, page * RESULTS_PER_PAGE);

  return (
    <div className="min-h-screen overflow-x-hidden" style={{ backgroundColor: '#fff' }}>
      {/* ───────── HOW IT WORKS — compact ───────── */}
      <section
        ref={bridgeRef}
        className="relative max-w-5xl mx-auto px-5 sm:px-6 lg:px-8 pt-6 pb-4 sm:pt-8 sm:pb-6"
      >
        <div
          aria-hidden
          className="absolute inset-x-0 top-0 bottom-0 -z-10"
          style={{
            background:
              'radial-gradient(80% 60% at 50% 0%, rgba(255,178,54,0.06), transparent 70%), radial-gradient(60% 50% at 50% 100%, rgba(240,90,53,0.06), transparent 70%)',
          }}
        />
        <div className="text-center mb-4 sm:mb-5">
          <span
            className="inline-block text-[10px] font-bold tracking-[0.18em] uppercase px-2.5 py-0.5 rounded-full mb-2"
            style={{ backgroundColor: 'var(--orange-3)', color: 'var(--orange-11)' }}
          >
            How it works
          </span>
          <h2
            className="text-wrap-safe mx-auto max-w-[300px] sm:max-w-none font-bold text-gray-900 mb-1"
            style={{ textWrap: 'balance', lineHeight: 1.2, fontSize: 'clamp(1rem, 2.2vw, 1.25rem)' }}
          >
            From browsing to confirmed booking
          </h2>
          <p
            className="text-wrap-safe mx-auto max-w-[300px] text-gray-600 sm:max-w-2xl"
            style={{ textWrap: 'pretty', lineHeight: 1.5, fontSize: '13px' }}
          >
            Three short steps — every provider on this list is verified.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-5 items-stretch md:items-center gap-2 sm:gap-y-4 md:gap-y-0">
          {[
            { n: 1, title: 'Pick a provider', desc: 'Use the filters to narrow providers.', Icon: MagnifyingGlassIcon },
            { n: 2, title: 'Browse verified providers', desc: 'Real ratings, real photos, real availability.', Icon: CheckBadgeIcon },
            { n: 3, title: 'Book with confidence', desc: 'Secure payment, reminders, and easy reschedule.', Icon: CalendarDaysIcon },
          ].map((step, i, arr) => (
            <React.Fragment key={step.n}>
              <div className="bridge-step md:col-span-1 flex items-center gap-3 rounded-xl border border-gray-100 bg-white/80 p-3 text-left shadow-sm md:flex-col md:gap-0 md:border-0 md:bg-transparent md:p-0 md:text-center md:shadow-none">
                <div className="relative flex-shrink-0">
                  <div
                    style={{
                      width: '40px', height: '40px', borderRadius: '13px',
                      background: 'linear-gradient(135deg, var(--orange-9), #FFB236)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff',
                      boxShadow: '0 8px 18px rgba(240,90,53,0.24)',
                    }}
                  >
                    <step.Icon style={{ width: '19px', height: '19px', strokeWidth: 1.8 }} />
                  </div>
                  <span
                    style={{
                      position: 'absolute', top: '-6px', right: '-6px',
                      width: '18px', height: '18px', borderRadius: '50%',
                      backgroundColor: '#fff', border: '2px solid var(--orange-9)',
                      color: 'var(--orange-9)', fontSize: '10px', fontWeight: 700,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                    }}
                  >
                    {step.n}
                  </span>
                </div>
                <div className="min-w-0 flex-1 md:flex-none">
                  <p className="text-wrap-safe font-semibold md:mt-2" style={{ color: 'var(--gray-12)', fontSize: '13px' }}>
                    {step.title}
                  </p>
                  <p className="text-wrap-safe mt-0.5 max-w-full md:max-w-[190px]" style={{ color: 'var(--gray-10)', fontSize: '12px' }}>
                    {step.desc}
                  </p>
                </div>
              </div>
              {i < arr.length - 1 && (
                <div className="bridge-connector md:col-span-1 hidden md:flex items-center justify-center px-2">
                  <div
                    style={{
                      width: '100%', height: '2px', borderRadius: '1px',
                      background: 'linear-gradient(90deg, var(--orange-6), var(--orange-9), var(--orange-6))',
                    }}
                  />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      </section>

      {/* ───────── PROVIDERS ───────── */}
      <section ref={providersRef} className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
        {(() => {
          const activeCategoryName =
            filters.category
              ? (categories.find((c) => String(c.id) === String(filters.category))?.name
                || (FALLBACK_SERVICES.find((s) => s.id === filters.category)?.name))
              : null;
          const count = filteredProviders.length;
          return (
            <div className="mb-8 text-center">
              <h2
                className="text-wrap-safe mx-auto max-w-[300px] sm:max-w-none font-bold text-gray-900 mb-1 sm:mb-2"
                style={{ textWrap: 'balance', lineHeight: 1.2, fontSize: 'clamp(1.05rem, 2.4vw, 1.35rem)' }}
              >
                {activeCategoryName
                  ? <>Providers offering <span style={{ color: 'var(--orange-9)' }}>{activeCategoryName}</span></>
                  : 'Providers near you'}
              </h2>
              <p
                className="text-wrap-safe mx-auto max-w-[300px] text-gray-600 sm:max-w-2xl"
                style={{ textWrap: 'pretty', lineHeight: 1.5, fontSize: '13px' }}
              >
                {loading
                  ? 'Loading verified providers near you…'
                  : count === 0
                    ? 'No matches yet — try clearing filters or a different category.'
                    : `${count} verified ${count === 1 ? 'provider' : 'providers'} ready to book.`}
              </p>

              {activeCategoryName && (
                <div className="mt-4 inline-flex items-center gap-2 pl-3 pr-1.5 py-1.5 rounded-full text-xs font-semibold"
                  style={{ backgroundColor: 'var(--orange-2)', border: '1px solid var(--orange-5)', color: 'var(--orange-11)' }}
                >
                  Filtered by: <strong>{activeCategoryName}</strong>
                  <button
                    type="button"
                    onClick={() => {
                      handleFilterChange('category', '');
                      const params = {};
                      Object.entries({ ...filters, category: '' }).forEach(([k, v]) => { if (v) params[k] = v; });
                      setSearchParams(params);
                    }}
                    title="Clear category filter"
                    style={{
                      width: '20px', height: '20px', borderRadius: '50%',
                      border: 'none', backgroundColor: 'var(--orange-9)', color: '#fff',
                      cursor: 'pointer', display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      fontSize: '12px', lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                </div>
              )}
            </div>
          );
        })()}

        {/* Compact search bar with inline icon-only filters */}
        <div className="mb-6 flex justify-center">
          {(() => {
            const setFilterAndUrl = (key, value) => {
              handleFilterChange(key, value);
              const params = {};
              Object.entries({ ...filters, [key]: value }).forEach(([k, v]) => {
                if (v) params[k] = v;
              });
              setSearchParams(params);
            };

            const FilterIcon = ({ id, Icon, label, active, children }) => (
              <div data-filter-pop style={{ position: 'relative' }}>
                <button
                  type="button"
                  onClick={() => setOpenFilter(openFilter === id ? null : id)}
                  title={label}
                  aria-label={label}
                  className="w-9 h-9 rounded-full flex items-center justify-center transition-colors"
                  style={{
                    backgroundColor: active ? 'var(--orange-9)' : '#fff',
                    color: active ? '#fff' : 'var(--gray-11)',
                    border: `1px solid ${active ? 'var(--orange-9)' : 'var(--gray-5)'}`,
                    cursor: 'pointer',
                    flexShrink: 0,
                    position: 'relative',
                  }}
                >
                  <Icon className="w-4 h-4" />
                  {active && (
                    <span
                      style={{
                        position: 'absolute',
                        top: '-2px',
                        right: '-2px',
                        width: '8px',
                        height: '8px',
                        borderRadius: '50%',
                        backgroundColor: '#fff',
                        border: '2px solid var(--orange-9)',
                      }}
                    />
                  )}
                </button>
                {openFilter === id && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 'calc(100% + 8px)',
                      right: 0,
                      zIndex: 40,
                      minWidth: '220px',
                      backgroundColor: '#fff',
                      border: '1px solid var(--gray-4)',
                      borderRadius: '12px',
                      boxShadow: '0 12px 32px rgba(0,0,0,0.10)',
                      padding: '12px',
                    }}
                  >
                    <p
                      style={{
                        fontSize: '11px',
                        fontWeight: 700,
                        textTransform: 'uppercase',
                        letterSpacing: '0.08em',
                        color: 'var(--gray-10)',
                        margin: '0 0 8px',
                        textAlign: 'left',
                      }}
                    >
                      {label}
                    </p>
                    {children}
                  </div>
                )}
              </div>
            );

            const fieldStyle = {
              width: '100%',
              padding: '7px 10px',
              borderRadius: '8px',
              border: '1px solid var(--gray-5)',
              backgroundColor: '#fff',
              color: 'var(--gray-12)',
              fontSize: '13px',
              outline: 'none',
            };

            const hasAny = filters.location || filters.rating || filters.mobile || filters.category || filters.q;

            return (
              <div
                className="flex w-full max-w-[640px] flex-col gap-2 sm:flex-row sm:items-center sm:gap-2 sm:px-2 sm:h-12 sm:rounded-full sm:border sm:bg-white sm:shadow-sm"
                style={{ borderColor: 'var(--gray-5)' }}
              >
                {/* Search input — its own pill on mobile (with submit button inside),
                    inline on desktop (submit lives at the end of the bar). */}
                <div
                  className="flex items-center gap-2 pl-4 pr-1 h-12 rounded-full border bg-white sm:p-0 sm:h-auto sm:rounded-none sm:border-0 sm:bg-transparent sm:flex-1 sm:min-w-0"
                  style={{ borderColor: 'var(--gray-5)' }}
                >
                  <MagnifyingGlassIcon className="w-4 h-4 shrink-0 sm:ml-2" style={{ color: 'var(--gray-9)' }} />
                  <input
                    type="text"
                    placeholder="Search providers…"
                    value={filters.q}
                    onChange={(e) => handleFilterChange('q', e.target.value)}
                    onKeyDown={handleKeyDown}
                    className="flex-1 bg-transparent text-sm focus:outline-none min-w-0 w-full"
                    style={{ color: 'var(--gray-12)' }}
                  />
                  {/* Mobile-only submit affordance, lives inside the input pill */}
                  <button
                    type="button"
                    onClick={applyFilters}
                    aria-label="Search"
                    className="sm:hidden inline-flex w-9 h-9 rounded-full items-center justify-center text-white shrink-0"
                    style={{ backgroundColor: 'var(--orange-9)' }}
                  >
                    <MagnifyingGlassIcon className="w-4 h-4" />
                  </button>
                </div>

                {/* Filters + Search button row — full width on mobile, inline on desktop */}
                <div className="flex items-center justify-between gap-2 sm:justify-start sm:gap-1 sm:flex-shrink-0">
                  {/* Divider line shown only when in unified pill mode (sm+) */}
                  <span
                    aria-hidden
                    className="hidden sm:inline-block self-center"
                    style={{ width: '1px', height: '20px', backgroundColor: 'var(--gray-4)' }}
                  />
                  <div className="flex items-center gap-1">
                  <FilterIcon
                    id="location"
                    Icon={MapPinIcon}
                    label="Location"
                    active={!!filters.location}
                  >
                    <input
                      type="text"
                      placeholder="e.g. Kampala"
                      value={filters.location}
                      onChange={(e) => handleFilterChange('location', e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          applyFilters();
                          setOpenFilter(null);
                        }
                      }}
                      style={fieldStyle}
                      autoFocus
                    />
                    {filters.location && (
                      <button
                        type="button"
                        onClick={() => setFilterAndUrl('location', '')}
                        className="mt-2 text-xs font-semibold"
                        style={{ color: 'var(--orange-9)' }}
                      >
                        Clear
                      </button>
                    )}
                  </FilterIcon>

                  <FilterIcon id="rating" Icon={StarIcon} label="Minimum rating" active={!!filters.rating}>
                    <select
                      value={filters.rating}
                      onChange={(e) => setFilterAndUrl('rating', e.target.value)}
                      style={fieldStyle}
                    >
                      <option value="">Any rating</option>
                      <option value="3">3.0 &amp; up</option>
                      <option value="4">4.0 &amp; up</option>
                      <option value="4.5">4.5 &amp; up</option>
                    </select>
                  </FilterIcon>

                  <FilterIcon id="type" Icon={TruckIcon} label="Service type" active={!!filters.mobile}>
                    <select
                      value={filters.mobile}
                      onChange={(e) => setFilterAndUrl('mobile', e.target.value)}
                      style={fieldStyle}
                    >
                      <option value="">All types</option>
                      <option value="true">Mobile (comes to you)</option>
                      <option value="false">In-shop only</option>
                    </select>
                  </FilterIcon>

                  <FilterIcon id="category" Icon={Squares2X2Icon} label="Category" active={!!filters.category}>
                    <select
                      value={filters.category}
                      onChange={(e) => setFilterAndUrl('category', e.target.value)}
                      style={fieldStyle}
                    >
                      <option value="">All categories</option>
                      {categories.map((cat) => (
                        <option key={cat.id} value={cat.id}>
                          {cat.name}
                        </option>
                      ))}
                    </select>
                  </FilterIcon>

                  {hasAny && (
                    <button
                      type="button"
                      onClick={() => {
                        setFilters({ q: '', category: '', location: '', rating: '', mobile: '' });
                        setSearchParams({});
                      }}
                      title="Clear all filters"
                      aria-label="Clear all filters"
                      className="w-9 h-9 rounded-full flex items-center justify-center transition-colors"
                      style={{
                        backgroundColor: '#fff',
                        color: 'var(--red-9, #e5484d)',
                        border: '1px solid var(--red-6, #fca5a5)',
                        cursor: 'pointer',
                        flexShrink: 0,
                      }}
                    >
                      <XMarkIcon className="w-4 h-4" />
                    </button>
                  )}
                </div>

                  {/* Search button — desktop pill only (mobile submit lives inside the input pill above). */}
                  <button
                    onClick={applyFilters}
                    aria-label="Search"
                    title="Search"
                    className="hidden sm:inline-flex h-9 px-5 rounded-full text-sm font-semibold text-white transition-colors items-center justify-center"
                    style={{ backgroundColor: 'var(--orange-9)', flexShrink: 0 }}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--orange-10)')}
                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'var(--orange-9)')}
                  >
                    Search
                  </button>
                </div>
              </div>
            );
          })()}
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Spinner size="large" />
          </div>
        ) : filteredProviders.length === 0 ? (
          <div
            className="text-center py-20 rounded-2xl border"
            style={{ borderColor: 'var(--gray-4)', backgroundColor: '#fff' }}
          >
            <div
              className="inline-flex items-center justify-center w-16 h-16 rounded-full mb-4"
              style={{ backgroundColor: 'var(--gray-3)' }}
            >
              <MagnifyingGlassIcon style={{ width: '28px', height: '28px', color: 'var(--gray-9)' }} />
            </div>
            <h3 className="text-lg font-semibold mb-2" style={{ color: 'var(--gray-12)' }}>
              No providers found
            </h3>
            <p className="text-sm mb-5" style={{ color: 'var(--gray-10)' }}>
              Try a different service or location
            </p>
            <button
              onClick={() => {
                setFilters({ q: '', category: '', location: '' });
                setSearchParams({});
              }}
              className="h-10 px-5 rounded-full text-sm font-semibold text-white"
              style={{ backgroundColor: 'var(--orange-9)' }}
            >
              Reset Search
            </button>
          </div>
        ) : (
          <>
            <ul className="divide-y" style={{ borderColor: 'var(--gray-4)' }}>
              {paginated.map((provider) => {
                const isExpanded = expandedIds.has(provider.id);
                const desc = provider.description || '';
                const isLong = desc.length > DESCRIPTION_LIMIT;
                const visibleDesc = !isLong || isExpanded ? desc : `${desc.slice(0, DESCRIPTION_LIMIT).trim()}…`;

                return (
                  <li key={provider.id} style={{ borderColor: 'var(--gray-4)' }}>
                    <Link
                      to={`/providers/${provider.id}`}
                      className="group relative flex gap-3 py-4 pr-11 sm:gap-4 sm:pr-0 transition-colors"
                      style={{ textDecoration: 'none' }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = 'var(--gray-2)')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                    >
                      {/* Avatar */}
                      <div className="shrink-0 pt-0.5 pl-2">
                        {provider.photoUrl ? (
                          <img
                            src={provider.photoUrl}
                            alt={provider.businessName}
                            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full object-cover"
                          />
                        ) : (
                          <div
                            className="w-10 h-10 sm:w-12 sm:h-12 rounded-full flex items-center justify-center text-sm sm:text-base font-bold"
                            style={{ backgroundColor: 'var(--orange-3)', color: 'var(--orange-9)' }}
                          >
                            {provider.businessName?.charAt(0)?.toUpperCase()}
                          </div>
                        )}
                      </div>

                      {/* Info */}
                      <div className="flex-1 min-w-0 pr-0 sm:pr-2">
                        {/* Row 1: Name + verified + rating */}
                        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 mb-1">
                          <div className="flex items-center gap-1.5 min-w-0 max-w-full">
                            <h3
                              className="text-[13px] sm:text-base font-semibold break-words"
                              style={{ color: 'var(--gray-12)' }}
                            >
                              {provider.businessName}
                            </h3>
                            {provider.verifiedBadge && (
                              <CheckBadgeIcon
                                className="w-4 h-4 shrink-0"
                                style={{ color: 'var(--orange-9)' }}
                                title="Verified provider"
                              />
                            )}
                          </div>

                          <div className="flex items-center gap-1 shrink-0">
                            <StarSolid style={{ width: '13px', height: '13px', color: '#facc15' }} />
                            <span className="text-xs font-semibold" style={{ color: 'var(--gray-12)' }}>
                              {(provider.rating || 0).toFixed(1)}
                            </span>
                            <span className="text-xs" style={{ color: 'var(--gray-9)' }}>
                              ({provider.totalReviews || 0})
                            </span>
                          </div>
                        </div>

                        {/* Row 2: Description + read more */}
                        {desc && (
                          <p
                            className="text-[11px] sm:text-[13px] leading-relaxed mb-3 break-words"
                            style={{ color: 'var(--gray-10)' }}
                          >
                            {visibleDesc}
                            {isLong && (
                              <button
                                type="button"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  toggleExpanded(provider.id);
                                }}
                                className="ml-1 text-[11px] sm:text-xs font-semibold transition-colors"
                                style={{ color: 'var(--orange-9)', textDecoration: 'underline' }}
                              >
                                {isExpanded ? 'Show less' : 'Read more'}
                              </button>
                            )}
                          </p>
                        )}

                        {/* Thin divider */}
                        <div className="border-t my-2" style={{ borderColor: 'var(--gray-4)' }} />

                        {/* Row 3: Location + services */}
                        <div className="flex min-w-0 items-center flex-wrap gap-x-3 gap-y-1">
                          <span className="inline-flex min-w-0 max-w-full items-center gap-1 text-[11px] sm:text-xs" style={{ color: 'var(--gray-10)' }}>
                            <MapPinIcon className="w-3.5 h-3.5 shrink-0" />
                            <span className="min-w-0 break-words">{provider.location || 'Location unavailable'}</span>
                          </span>

                          {provider.serviceCount > 0 && (
                            <span className="text-[11px] sm:text-xs" style={{ color: 'var(--gray-10)' }}>
                              {provider.serviceCount} service{provider.serviceCount !== 1 ? 's' : ''}
                            </span>
                          )}

                          {provider.offersMobile && (
                            <span
                              className="text-[11px] font-semibold px-2 py-0.5 rounded-full"
                              style={{ backgroundColor: 'var(--orange-2)', color: 'var(--orange-10)' }}
                            >
                              Mobile
                            </span>
                          )}
                        </div>
                      </div>

                      {/* Actions */}
                      <div className="absolute right-2 top-4 shrink-0 flex items-center gap-2 sm:static sm:self-center sm:pr-2">
                        <button
                          type="button"
                          onClick={(e) => handleToggleFavorite(provider, e)}
                          title={favoriteIds.has(provider.id) ? 'Remove from favorites' : 'Add to favorites'}
                          aria-label={favoriteIds.has(provider.id) ? 'Remove from favorites' : 'Add to favorites'}
                          className="w-8 h-8 rounded-full flex items-center justify-center transition-colors"
                          style={{
                            border: '1px solid var(--gray-5)',
                            backgroundColor: favoriteIds.has(provider.id) ? 'var(--orange-2)' : '#fff',
                            color: favoriteIds.has(provider.id) ? 'var(--orange-9)' : 'var(--gray-10)',
                            cursor: 'pointer',
                          }}
                        >
                          {favoriteIds.has(provider.id) ? (
                            <HeartSolid style={{ width: '16px', height: '16px' }} />
                          ) : (
                            <HeartOutline style={{ width: '16px', height: '16px' }} />
                          )}
                        </button>
                        <ArrowRightIcon
                          className="w-4 h-4 transition-transform group-hover:translate-x-1"
                          style={{ color: 'var(--gray-10)' }}
                        />
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>

            {/* Result count + bottom pagination */}
            {filteredProviders.length > 0 && (
              <p
                className="text-xs text-center mt-8"
                style={{ color: 'var(--gray-10)' }}
              >
                Showing{' '}
                <span style={{ fontWeight: 600, color: 'var(--gray-12)' }}>
                  {(page - 1) * RESULTS_PER_PAGE + 1}–{Math.min(page * RESULTS_PER_PAGE, filteredProviders.length)}
                </span>{' '}
                of{' '}
                <span style={{ fontWeight: 600, color: 'var(--gray-12)' }}>
                  {filteredProviders.length}
                </span>
              </p>
            )}

            {totalPages > 1 && (
              <div
                className="flex items-center justify-center gap-2 mt-4 pt-6 border-t"
                style={{ borderColor: 'var(--gray-4)' }}
              >
                <button
                  onClick={() => setPage((p) => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="h-9 px-4 rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-1"
                  style={{
                    backgroundColor: '#fff',
                    color: 'var(--gray-11)',
                    border: '1px solid var(--gray-5)',
                    opacity: page === 1 ? 0.4 : 1,
                    cursor: page === 1 ? 'default' : 'pointer',
                  }}
                >
                  <ChevronLeftIcon className="w-4 h-4" />
                  Previous
                </button>
                {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                  <button
                    key={p}
                    onClick={() => setPage(p)}
                    className="w-9 h-9 rounded-lg text-sm transition-colors"
                    style={{
                      fontWeight: page === p ? 700 : 500,
                      backgroundColor: page === p ? 'var(--orange-9)' : '#fff',
                      color: page === p ? '#fff' : 'var(--gray-11)',
                      border: page === p ? '1px solid var(--orange-9)' : '1px solid var(--gray-5)',
                    }}
                  >
                    {p}
                  </button>
                ))}
                <button
                  onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="h-9 px-4 rounded-lg text-sm font-medium transition-colors inline-flex items-center gap-1"
                  style={{
                    backgroundColor: '#fff',
                    color: 'var(--gray-11)',
                    border: '1px solid var(--gray-5)',
                    opacity: page === totalPages ? 0.4 : 1,
                    cursor: page === totalPages ? 'default' : 'pointer',
                  }}
                >
                  Next
                  <ChevronRightIcon className="w-4 h-4" />
                </button>
              </div>
            )}
          </>
        )}
      </section>


    </div>
  );
};

export default Search;
